import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { adminDevicesService } from '../../api/apiClient';

function maskCode(code) {
  if (!code) return '—';
  return '••••';
}

function isValidCode(code) {
  return /^\d{4}$/.test(String(code || '').trim());
}

export default function UserDevicesDialog({ open, user, onClose, onSnackbar }) {
  const userId = user?.userId;
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [error, setError] = useState('');
  const [showCodes, setShowCodes] = useState(false);

  // Per-device edit state for service panel codes
  const [codeDrafts, setCodeDrafts] = useState({});
  const [confirmReset, setConfirmReset] = useState({ open: false, deviceUuid: null });

  const loadDevices = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    const resp = await adminDevicesService.listUserDevices(userId);
    setLoading(false);

    if (!resp.success) {
      setError(resp.message || 'Failed to load devices');
      onSnackbar?.({ message: resp.message || 'Failed to load devices', severity: 'error' });
      return;
    }

    const list = resp.data?.data?.devices || resp.data?.devices || [];
    const arr = Array.isArray(list) ? list : [];
    setDevices(arr);

    // Initialize drafts
    const drafts = {};
    for (const d of arr) {
      drafts[d.deviceUuid] = d.servicePanelCode || '';
    }
    setCodeDrafts(drafts);
  }, [onSnackbar, userId]);

  useEffect(() => {
    if (!open) return;
    setShowCodes(false);
    setDevices([]);
    setCodeDrafts({});
    setConfirmReset({ open: false, deviceUuid: null });
    loadDevices();
  }, [open, loadDevices]);

  const handleClose = () => {
    if (loading) return;
    onClose?.();
  };

  const handleSetCode = useCallback(async (deviceUuid) => {
    const code = String(codeDrafts[deviceUuid] || '').trim();
    if (!isValidCode(code)) {
      onSnackbar?.({ message: 'Service panel code must be exactly 4 digits', severity: 'error' });
      return;
    }
    setLoading(true);
    const resp = await adminDevicesService.setServicePanelCode(deviceUuid, code);
    setLoading(false);

    if (!resp.success) {
      onSnackbar?.({ message: resp.message || 'Failed to update service panel code', severity: 'error' });
      return;
    }
    onSnackbar?.({ message: resp.message || 'Service panel code updated', severity: 'success' });
    await loadDevices();
  }, [codeDrafts, loadDevices, onSnackbar]);

  const handleToggleActive = useCallback(async (deviceUuid, nextIsActive) => {
    setLoading(true);
    const resp = await adminDevicesService.updateDevice(deviceUuid, { isActive: nextIsActive });
    setLoading(false);
    if (!resp.success) {
      onSnackbar?.({ message: resp.message || 'Failed to update device', severity: 'error' });
      return;
    }
    onSnackbar?.({ message: resp.message || 'Device updated', severity: 'success' });
    await loadDevices();
  }, [loadDevices, onSnackbar]);

  const handleConfirmReset = (deviceUuid) => {
    setConfirmReset({ open: true, deviceUuid });
  };

  const handleResetBinding = useCallback(async () => {
    const deviceUuid = confirmReset.deviceUuid;
    if (!deviceUuid) return;
    setLoading(true);
    const resp = await adminDevicesService.resetBinding(deviceUuid);
    setLoading(false);
    setConfirmReset({ open: false, deviceUuid: null });
    if (!resp.success) {
      onSnackbar?.({ message: resp.message || 'Failed to reset binding', severity: 'error' });
      return;
    }
    onSnackbar?.({ message: resp.message || 'Device binding reset', severity: 'success' });
    await loadDevices();
  }, [confirmReset.deviceUuid, loadDevices, onSnackbar]);

  const title = useMemo(() => {
    const label = user?.sabalLoginEmail || (userId ? `User #${userId}` : 'User');
    return `Devices — ${label}`;
  }, [user?.sabalLoginEmail, userId]);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth aria-labelledby="user-devices-dialog-title">
        <DialogTitle id="user-devices-dialog-title">{title}</DialogTitle>
        <DialogContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Service panel codes are masked by default.
              </Typography>
              <IconButton size="small" onClick={() => setShowCodes((v) => !v)} aria-label="toggle code visibility">
                {showCodes ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
              <Typography variant="body2">{showCodes ? 'Hide codes' : 'Show codes'}</Typography>
            </Stack>
            <Button variant="outlined" onClick={loadDevices} disabled={loading}>
              Refresh
            </Button>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {devices.length === 0 ? (
            <Alert severity="info">No devices found for this user.</Alert>
          ) : (
            <Stack spacing={2}>
              {devices.map((d) => (
                <Paper key={d.deviceUuid} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 260 }}>
                      <Typography variant="caption" color="text.secondary">Device UUID</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                        {d.deviceUuid}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                        <Chip size="small" color={d.isActive ? 'success' : 'default'} label={d.isActive ? 'Active' : 'Inactive'} />
                        <Chip size="small" label={`tokenVersion ${d.tokenVersion ?? '—'}`} />
                      </Stack>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 260 }}>
                      <Typography variant="caption" color="text.secondary">Service Panel Code</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {showCodes ? (d.servicePanelCode || '—') : maskCode(d.servicePanelCode)}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                        <TextField
                          size="small"
                          label="New code"
                          value={codeDrafts[d.deviceUuid] ?? ''}
                          onChange={(e) => setCodeDrafts((p) => ({ ...p, [d.deviceUuid]: e.target.value }))}
                          disabled={loading}
                          error={!!codeDrafts[d.deviceUuid] && !isValidCode(codeDrafts[d.deviceUuid])}
                          helperText="4 digits"
                        />
                        <Button
                          variant="contained"
                          size="small"
                          disabled={loading || !isValidCode(codeDrafts[d.deviceUuid])}
                          onClick={() => handleSetCode(d.deviceUuid)}
                        >
                          Change code
                        </Button>
                      </Stack>
                    </Box>

                    <Box sx={{ minWidth: 220 }}>
                      <Typography variant="caption" color="text.secondary">Admin actions</Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                        <Switch
                          checked={!!d.isActive}
                          disabled={loading}
                          onChange={(e) => handleToggleActive(d.deviceUuid, e.target.checked)}
                        />
                        <Typography variant="body2">{d.isActive ? 'Active' : 'Inactive'}</Typography>
                      </Stack>
                      <Button
                        color="error"
                        variant="outlined"
                        size="small"
                        disabled={loading}
                        onClick={() => handleConfirmReset(d.deviceUuid)}
                        sx={{ mt: 1 }}
                      >
                        Reset binding
                      </Button>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="outlined" disabled={loading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmReset.open}
        onClose={() => setConfirmReset({ open: false, deviceUuid: null })}
        maxWidth="xs"
        fullWidth
        aria-labelledby="confirm-reset-binding-title"
      >
        <DialogTitle id="confirm-reset-binding-title">Reset device binding?</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            This will unbind the device from the user and invalidate its device token (tokenVersion++).
          </Alert>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Device UUID:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
            {confirmReset.deviceUuid || '—'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmReset({ open: false, deviceUuid: null })}
            variant="outlined"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetBinding}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Reset binding
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


